type UmamiData = Record<string, any>;

export enum AnalyticEvent {
	PageView = "Page view",
	Clicked = "Clicked",
	UserLogin = "User login",
	UserLogout = "User logout",
}

export function trackEvent<T extends UmamiData = UmamiData>(
	eventName: AnalyticEvent | string,
	data?: T
): void {
	const userData = {
		name: `${window.analyticsUser?.MemberFirstName} ${window.analyticsUser?.MemberLastName}`,
		username: window.analyticsUser?.USERID,
	};
	if (window.umami) {
		if (data !== undefined) {
			// console.log(`Tracking event: ${eventName}`, {
			// 	...userData,
			// 	...data,
			// });
			window.umami.track(eventName, data);
		} else {
			// console.log(`Tracking event: ${eventName}`);
			window.umami.track(eventName, userData);
		}
	}
}

export function trackPost(path: string, payload: Record<string, any>): void {
	for (const handler of pathHandlers) {
		if (handler.pathTest(path, payload)) {
			const eventName = handler.eventName(payload);
			const eventPayload = handler.payloadBuilder(payload);
			trackEvent(eventName, { ...eventPayload });
			break;
		}
	}
}

type PayloadBuilder = (
	payload: Record<string, any>
) => Record<string, any> | undefined | null;

const pathHandlers: {
	pathTest: (path: string, payload: Record<string, any>) => boolean;
	eventName: (payload: Record<string, any>) => string;
	payloadBuilder: PayloadBuilder;
}[] = [
	{
		pathTest: (path) =>
			path.includes("NoticeDefinitions") && !path.endsWith("/q"),
		eventName: (payload) => {
			const noticeType =
				typeof payload.noticeType === "string"
					? payload.noticeType.split(".").pop()
					: payload.noticeType;
			return `Notice Sent - ${noticeType}`;
		},
		payloadBuilder: (payload) => ({
			complianceCaseId: payload.complianceCaseId,
			noticeType: payload.noticeType,
			ccAor: payload.ccAor,
			ccOffice: payload.ccOffice,
			includeMember: payload.ccMember,
			violationsCount: payload.violations?.length || 0,
			suspensionDate: payload.form?.autoSuspension,
			attachments: payload.fileAttachments?.length || 0,
		}),
	},
	{
		pathTest: (path) => path.includes("complaints/sendComplaint"),
		eventName: (_payload) => {
			return "Complaint Sent";
		},
		payloadBuilder: (_payload) => undefined,
	},
	{
		pathTest: (path) => path.includes("KPIs"),
		eventName: (payload) => {
			if (
				payload &&
				typeof payload.memberId === "string" &&
				payload.memberId.length > 0
			) {
				return "KPI Member Searched";
			}
			return "KPI AOR Searched";
		},
		payloadBuilder: (_payload) => undefined,
	},
	makeQBoardHandler({
		pathIncludes: "Reports/q",
		eventName: "Report Board Searched",
		extraPathTest: (path: string, _payload: Record<string, any>) =>
			typeof window !== "undefined" &&
			!!window.location &&
			window.location.href.includes("compliance/report-queue"),
	}),
	makeQBoardHandler({
		pathIncludes: "ComplianceCases/q",
		eventName: "Case Board Searched",
	}),
	makeQBoardHandler({
		pathIncludes: "WorkItem/q",
		eventName: "Assist - Ticket Search",
	}),
	makeQBoardHandler({
		pathIncludes: "CustomReports/q",
		eventName: "Insights Board Searched",
	}),
	makeQBoardHandler({
		pathIncludes: "MLSRules/q",
		eventName: "List Management - Rules Board Searched",
		extraPathTest: (path: string, _payload: Record<string, any>) =>
			typeof window !== "undefined" &&
			!!window.location &&
			window.location.href.includes("compliance/list-management"),
	}),
	makeQBoardHandler({
		pathIncludes: "ReportReasonCategories/q",
		eventName: "List Management - Report Reason Categories Board Searched",
		extraPathTest: (path: string, _payload: Record<string, any>) =>
			typeof window !== "undefined" &&
			!!window.location &&
			window.location.href.includes("compliance/list-management"),
	}),
	makeQBoardHandler({
		pathIncludes: "ReportReasons/q",
		eventName: "List Management - Report Reasons Board Searched",
		extraPathTest: (path: string, _payload: Record<string, any>) =>
			typeof window !== "undefined" &&
			!!window.location &&
			window.location.href.includes("compliance/list-management"),
	}),
	makeQBoardHandler({
		pathIncludes: "EnumerationLookups/q",
		eventName: "List Management - Dispositions Board Searched",
		extraPathTest: (path: string, _payload: Record<string, any>) =>
			typeof window !== "undefined" &&
			!!window.location &&
			window.location.href.includes("compliance/list-management"),
	}),
	makeQBoardHandler({
		pathIncludes: "Headlines/q",
		eventName: "List Management - Note Headlines Board Searched",
		extraPathTest: (path: string, _payload: Record<string, any>) =>
			typeof window !== "undefined" &&
			!!window.location &&
			window.location.href.includes("compliance/list-management"),
	}),
	makeQBoardHandler({
		pathIncludes: "Reminders/q",
		eventName: "Reminder Board Searched",
	}),
	{
		pathTest: (path) => path.includes("CaseNotes") && !path.endsWith("/q"),
		eventName: (payload) =>
			`Case ${payload.headline.isTask ? "Task" : "Note"} Created`,
		payloadBuilder: (payload) => {
			const result: Record<string, any> = {};
			if (payload && payload.reminderDate) {
				result.reminderDate = true;
			}
			result.caseNumber = payload.caseNumber;
			result.headline = payload.headline.name;
			result.isTask = payload.headline.isTask;
			result.wasSharedToCases =
				Array.isArray(payload.caseNumbersToShareWith) &&
				payload.caseNumbersToShareWith.length > 0;
			result.wasSharedWithAnalysts =
				Array.isArray(payload.emailAddressesToShareWith) &&
				payload.emailAddressesToShareWith.length > 0;
			result.sharedToCasesCount = Array.isArray(
				payload.caseNumbersToShareWith
			)
				? payload.caseNumbersToShareWith.length
				: 0;
			result.sharedWithAnalystsCount = Array.isArray(
				payload.emailAddressesToShareWith
			)
				? payload.emailAddressesToShareWith.length
				: 0;
			return result;
		},
	},
	{
		pathTest: (path, payload) => {
			// Fix: path may be lowercase, and payload may not have criteria
			if (!path.toLowerCase().includes("casehistorys/q")) return false;
			const criteria = Array.isArray(payload?.criteria)
				? payload.criteria
				: [];
			// Fix: check for eventName field in any criteria, not just the first
			return criteria.some(
				(c) =>
					c.field === "eventName" &&
					Array.isArray(c.values) &&
					c.values.length > 0
			);
		},
		eventName: (payload) => "Case History Searched",
		payloadBuilder: (payload) => {
			const criteria = Array.isArray(payload.criteria)
				? payload.criteria
				: [];
			const eventNameCriterion = criteria.find(
				(c) =>
					c.field === "eventName" &&
					Array.isArray(c.values) &&
					c.values.length > 0
			);
			if (eventNameCriterion) {
				return { eventName: eventNameCriterion.values[0] };
			}
			return undefined;
		},
	},
];

function makeQBoardHandler({
	pathIncludes,
	eventName,
	extraPathTest,
}: {
	pathIncludes: string;
	eventName: string;
	extraPathTest?: (path: string, payload: Record<string, any>) => boolean;
}): {
	pathTest: (path: string, payload: Record<string, any>) => boolean;
	eventName: (payload: Record<string, any>) => string;
	payloadBuilder: PayloadBuilder;
} {
	return {
		pathTest: (path: string, payload: Record<string, any>) => {
			if (!path.includes(pathIncludes)) return false;
			if (extraPathTest && !extraPathTest(path, payload)) return false;
			const criteriaArr: Array<{ field?: string }> = Array.isArray(
				payload.criteria
			)
				? payload.criteria
				: [];
			const fields = criteriaArr
				.map((c) => (typeof c.field === "string" ? c.field : undefined))
				.filter((f): f is string => !!f);
			if (fields.length === 0) return false;
			return true;
		},
		eventName: () => eventName,
		payloadBuilder: (payload: Record<string, any>) => {
			const criteriaArr: Array<{ field?: string; values?: any[] }> =
				Array.isArray(payload.criteria) ? payload.criteria : [];
			const fieldsWithValues = criteriaArr
				.filter(
					(c) =>
						typeof c.field === "string" &&
						Array.isArray(c.values) &&
						c.values.length > 0
				)
				.map((c) => ({
					field: c.field as string,
					value:
						Array.isArray(c.values) && c.values.length > 0
							? c.values[0]
							: undefined,
				}));
			return { fields: fieldsWithValues };
		},
	};
}
